import { useMemo, useState } from 'react';
import type { MouseEvent } from 'react';

import {
  useAffinityListConfigs,
  useAffinityListFields,
  useCreateForm,
  useDeleteForm,
  useForms,
  useUpdateForm,
} from '../../hooks/useOutreach';
import type { FormSessionIn, OutreachForm } from '../../types/outreach';

import type { SessionDraft } from './formSessions';
import {
  CLEARED_WRITEBACK,
  OPTION_UNSET,
  emptySession,
  listScopedDropdownFields,
  reservedFieldIdsForList,
  slugifySessionKey,
} from './formSessions';
import { formatEt } from '../../utils/formatTimestamp';
import styles from '../pages/OutreachPage.module.css';
import { RefreshButton } from './RefreshButton';

// Base URL for the iframe embed snippet. Uses the current origin so
// dev shows localhost (harmless — dev users won't actually paste it
// into a public site). In prod the admin UI runs on the same origin
// as the public form, so this resolves to connect.ember-infra.com.
function embedSnippetForSlug(slug: string): string {
  const origin =
    typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : 'https://connect.ember-infra.com';
  return (
    `<iframe src="${origin}/forms/${slug}" ` +
    `width="100%" height="600" style="border:0;" ` +
    `title="RSVP form"></iframe>`
  );
}

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Fall through to legacy path.
  }
  // Legacy fallback for older browsers / non-secure contexts.
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}


/**
 * Top-level Outreach > Forms tab — form *definitions*.
 *
 * A form is the recipient-facing thing (URL slug, headline, dietary
 * + comments toggles). Mapping a form to one or many lists is what
 * tells the engine "submissions to this form write back to these
 * Affinity lists." Distinct from ResponsesAdmin (which lists actual
 * submissions).
 *
 * Linking >1 list triggers a confirm prompt — multi-list write-back
 * is supported (every match writes), but it's a meaningful change in
 * behavior that should be explicit.
 */
export function FormsAdmin() {
  const formsQ = useForms();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [copiedSlug, setCopiedSlug] = useState<string | null>(null);

  async function handleCopyEmbed(slug: string, e: MouseEvent<HTMLButtonElement>) {
    // Stop propagation so the row click (→ editor) doesn't fire.
    e.stopPropagation();
    const ok = await copyToClipboard(embedSnippetForSlug(slug));
    if (ok) {
      setCopiedSlug(slug);
      setTimeout(() => {
        setCopiedSlug((curr) => (curr === slug ? null : curr));
      }, 2000);
    }
  }

  if (creating) {
    return (
      <FormEditor
        onClose={() => setCreating(false)}
      />
    );
  }
  if (editingId != null) {
    const form = formsQ.data?.find((f) => f.id === editingId);
    if (!form) {
      // Form was just deleted underneath us; bounce back to the list.
      setEditingId(null);
      return null;
    }
    return (
      <FormEditor
        form={form}
        onClose={() => setEditingId(null)}
      />
    );
  }

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Forms</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <RefreshButton
            onClick={() => formsQ.refetch()}
            isFetching={formsQ.isFetching}
            title="Re-fetch form definitions"
          />
          <button
            type="button"
            className={`${styles.button} ${styles.buttonPrimary}`}
            onClick={() => setCreating(true)}
          >
            + New form
          </button>
        </div>
      </div>

      <div className={styles.helpText} style={{ marginBottom: 12 }}>
        Forms are the recipient-facing pages. Each form has its own URL
        (<code>/forms/&lt;slug&gt;</code>) and can be linked to one or more
        lists. Submissions write back to every linked list.
      </div>

      {formsQ.isLoading ? (
        <div className={styles.empty}>Loading…</div>
      ) : (formsQ.data?.length ?? 0) === 0 ? (
        <div className={styles.empty}>
          No forms yet. Click <strong>+ New form</strong> to create one.
        </div>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Slug / URL</th>
              <th>Name</th>
              <th>Headline</th>
              <th>Fields</th>
              <th>Lists</th>
              <th>Created</th>
              <th>Embed</th>
            </tr>
          </thead>
          <tbody>
            {formsQ.data?.map((f) => (
              <tr
                key={f.id}
                onClick={() => setEditingId(f.id)}
                style={{ cursor: 'pointer' }}
              >
                <td className={styles.code}>
                  /forms/{f.slug}
                </td>
                <td>{f.name}</td>
                <td>{f.headline}</td>
                <td className={styles.code}>
                  {[
                    'first/last/email/yes/no',
                    f.dietary_enabled ? 'dietary' : null,
                    f.comments_enabled ? 'comments' : null,
                    f.sessions.length
                      ? `${f.sessions.length} session${f.sessions.length === 1 ? '' : 's'}`
                      : null,
                  ].filter(Boolean).join(' · ')}
                </td>
                <td>
                  <span style={{ marginRight: f.list_ids.length >= 2 ? 6 : 0 }}>
                    {f.list_ids.length}
                  </span>
                  {f.list_ids.length >= 2 && (
                    <span
                      style={{
                        fontSize: 10,
                        fontFamily: "'DM Mono', monospace",
                        color: '#7B5400',
                        background: '#FFF7ED',
                        border: '1px solid #FBD38D',
                        padding: '1px 6px',
                        borderRadius: 8,
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                      }}
                      title={
                        `This form writes back to ${f.list_ids.length} ` +
                        'linked lists. Submissions update status / dietary / ' +
                        'comments on every linked list.'
                      }
                    >
                      Multi-list
                    </span>
                  )}
                </td>
                <td className={styles.code}>{formatEt(f.created_at)}</td>
                <td>
                  <button
                    type="button"
                    className={styles.button}
                    onClick={(e) => handleCopyEmbed(f.slug, e)}
                    style={{ fontSize: 12 }}
                    title="Copy <iframe> snippet for embedding this form on a website"
                  >
                    {copiedSlug === f.slug ? '✓ Copied' : 'Copy iframe'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}


/**
 * Inline editor — used for both create + edit. Edit pre-fills from
 * `form`; create starts empty.
 */
function FormEditor({
  form,
  onClose,
}: {
  form?: OutreachForm;
  onClose: () => void;
}) {
  const isEdit = form != null;
  const listsQ = useAffinityListConfigs();
  const createMut = useCreateForm();
  const updateMut = useUpdateForm();
  const deleteMut = useDeleteForm();

  const [slug, setSlug] = useState(form?.slug || '');
  const [name, setName] = useState(form?.name || '');
  const [headline, setHeadline] = useState(form?.headline || '');
  const [dietaryEnabled, setDietaryEnabled] = useState(
    form?.dietary_enabled ?? true,
  );
  const [commentsEnabled, setCommentsEnabled] = useState(
    form?.comments_enabled ?? true,
  );
  const [listIds, setListIds] = useState<number[]>(form?.list_ids || []);
  const [sessions, setSessions] = useState<SessionDraft[]>(
    (form?.sessions || []).map((s) => ({
      session_key: s.session_key,
      label: s.label,
      when_text: s.when_text || '',
      where_text: s.where_text || '',
      is_primary: s.is_primary,
      affinity_field_id: s.affinity_field_id ?? null,
      yes_option_label: s.yes_option_label,
      no_option_label: s.no_option_label,
      dropdown_options: s.dropdown_options || {},
      crm_field_key: s.crm_field_key ?? null,
      // Saved rows never re-slug: their key is already load-bearing.
      keyTouched: true,
    })),
  );
  const [contactEmail, setContactEmail] = useState(form?.contact_email || '');
  const [notOnListMessage, setNotOnListMessage] = useState(
    form?.not_on_list_message || '',
  );
  const [blockedMessage, setBlockedMessage] = useState(
    form?.blocked_message || '',
  );
  const [error, setError] = useState<string | null>(null);

  const listsByIds = useMemo(() => {
    const m = new Map<number, string>();
    for (const l of listsQ.data || []) {
      m.set(l.affinity_list_id, l.label || l.list_slug);
    }
    return m;
  }, [listsQ.data]);

  // ⭐ REVIEW F3. A session's `affinity_field_id` is LIST-SCOPED, so the moment
  // the linked list changes every stored field id, option map and option label
  // is about someone else's list. Keeping them would submit an id that at best
  // does not exist on the new list and at worst IS its Status field. The label
  // and the when/where text are the admin's own copy and survive.
  // ⚠ Resets the labels to the 'Yes'/'No' DEFAULTS, not to OPTION_UNSET, so
  // that "this row has no write-back target" has exactly ONE representation —
  // the same one `pickSessionField('')` produces.
  //
  // OPTION_UNSET here was a real bug: `validateSessions` skips rows whose field
  // id is null, so nothing caught the empty labels, `sessionsPayload` sent them
  // verbatim, and `FormSessionIn.yes_option_label` is `min_length=1` — so
  // unlinking one list, linking another and saving WITHOUT re-picking (entirely
  // legitimate for the primary row, which normally has no field at all) 422'd
  // with a raw Pydantic message.
  function clearSessionWritebackTargets() {
    setSessions((curr) => curr.map((sess) => (
      sess.affinity_field_id == null ? sess : {
        ...sess,
        ...CLEARED_WRITEBACK,
      }
    )));
  }

  function toggleListId(id: number) {
    clearSessionWritebackTargets();
    setListIds((curr) => {
      const has = curr.includes(id);
      const next = has ? curr.filter((x) => x !== id) : [...curr, id];
      // Multi-list warning: explicit confirm before crossing 1 -> 2.
      // Single-list flows are unaffected; 2+ means submissions write
      // back to every mapped list (and the user should know).
      if (!has && next.length === 2) {
        const ok = window.confirm(
          'Linking this form to a SECOND list means every submission will ' +
          'write back to BOTH lists (status, dietary, comments). ' +
          'Continue?',
        );
        if (!ok) return curr;
      }
      return next;
    });
  }

  // The per-session Affinity fields are list-scoped, so sessions are only
  // configurable on a form mapped to EXACTLY one list (server enforces the
  // same rule with a 400).
  const singleListId = listIds.length === 1 ? listIds[0] : null;
  const sessionFieldsQ = useAffinityListFields(singleListId);
  // ⭐ REVIEW F4. The list config's own Status / dietary / comments / scope /
  // suppression fields are never offered — Status above all, since the session
  // write runs AFTER the status write with no guard between them.
  const reservedIds = useMemo(
    () => reservedFieldIdsForList(
      (listsQ.data || []).find((l) => l.affinity_list_id === singleListId),
    ),
    [listsQ.data, singleListId],
  );
  const sessionFields = useMemo(
    () => listScopedDropdownFields(
      sessionFieldsQ.data, singleListId, reservedIds,
    ),
    [sessionFieldsQ.data, singleListId, reservedIds],
  );

  function patchSession(idx: number, patch: Partial<SessionDraft>) {
    setSessions((curr) =>
      curr.map((s, i) => (i === idx ? { ...s, ...patch } : s)),
    );
  }

  function setSessionLabel(idx: number, label: string) {
    setSessions((curr) =>
      curr.map((s, i) =>
        i === idx
          ? {
              ...s,
              label,
              session_key: s.keyTouched ? s.session_key : slugifySessionKey(label),
            }
          : s,
      ),
    );
  }

  function setPrimary(idx: number) {
    // Exactly one primary — selecting a row clears the others rather than
    // letting the admin save a shape the server will reject.
    setSessions((curr) => curr.map((s, i) => ({ ...s, is_primary: i === idx })));
  }

  function moveSession(idx: number, delta: number) {
    setSessions((curr) => {
      const target = idx + delta;
      if (target < 0 || target >= curr.length) return curr;
      const next = [...curr];
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  }

  function pickSessionField(idx: number, fieldIdRaw: string) {
    if (!fieldIdRaw) {
      patchSession(idx, { ...CLEARED_WRITEBACK });
      return;
    }
    const field = sessionFields.find((f) => String(f.id) === fieldIdRaw);
    if (!field) return;
    const options: Record<string, number> = {};
    for (const o of field.dropdown_options) options[o.text] = o.id;
    const labels = field.dropdown_options.map((o) => o.text);
    const curr = sessions[idx];
    patchSession(idx, {
      affinity_field_id: field.id,
      dropdown_options: options,
      // ⭐ REVIEW F2. Keep a label only if the NEW field actually carries it;
      // otherwise leave it UNSET. Never seed from `labels[0]`/`labels[1]`:
      // Affinity's own order on the real field is
      // `No Response, RSVP'd Attending, RSVP'd Not Attending`, so that default
      // silently maps Yes -> "No Response" for anyone who saves without
      // opening the selects.
      yes_option_label: labels.includes(curr?.yes_option_label)
        ? curr.yes_option_label
        : OPTION_UNSET,
      no_option_label: labels.includes(curr?.no_option_label)
        ? curr.no_option_label
        : OPTION_UNSET,
    });
  }

  function sessionsPayload(): FormSessionIn[] {
    return sessions.map((s) => ({
      session_key: s.session_key,
      label: s.label,
      when_text: s.when_text || null,
      where_text: s.where_text || null,
      is_primary: s.is_primary,
      affinity_field_id: s.affinity_field_id ?? null,
      // Belt AND braces with CLEARED_WRITEBACK: a label is only meaningful
      // alongside a field id, and `FormSessionIn` rejects an empty one
      // (`min_length=1`). Sending '' could only ever produce a raw 422, so the
      // payload floor is the defaults — whatever route left the row unset.
      yes_option_label: s.yes_option_label || 'Yes',
      no_option_label: s.no_option_label || 'No',
      dropdown_options: s.dropdown_options,
      crm_field_key: s.crm_field_key ?? null,
    }));
  }

  function validateSessions(): string | null {
    if (sessions.length === 0) return null;
    if (listIds.length !== 1) {
      return 'A form with sessions must be linked to exactly one list.';
    }
    for (const s of sessions) {
      if (!s.label.trim()) return 'Every session needs a label.';
      if (!/^[a-z0-9][a-z0-9_]{0,31}$/.test(s.session_key)) {
        return `Session key "${s.session_key}" must be lowercase letters, digits and underscores.`;
      }
    }
    const keys = sessions.map((s) => s.session_key);
    const dupe = keys.find((k, i) => keys.indexOf(k) !== i);
    if (dupe) return `Duplicate session key "${dupe}".`;
    if (sessions.filter((s) => s.is_primary).length !== 1) {
      return 'Exactly one session must be marked primary.';
    }
    // ⭐ REVIEW F2. Both option labels must be chosen, and must differ. Equal
    // labels write the same option for Yes and for No, which reads back as a
    // real answer — the data is lost with nothing to notice. The server
    // enforces the same two rules; this just fails faster and in words.
    for (const s of sessions) {
      if (s.affinity_field_id == null) continue;
      if (!s.yes_option_label || !s.no_option_label) {
        return `Session "${s.label || s.session_key}": pick which Affinity `
          + 'option means Yes and which means No.';
      }
      if (s.yes_option_label === s.no_option_label) {
        return `Session "${s.label || s.session_key}": the Yes and No options `
          + 'must be different.';
      }
    }
    return null;
  }

  async function handleSave() {
    setError(null);
    if (!slug.match(/^[a-z0-9][a-z0-9-]{0,62}$/)) {
      setError('Slug must be lowercase, alphanumeric + hyphens only.');
      return;
    }
    if (!name.trim()) {
      setError('Name is required.');
      return;
    }
    if (!headline.trim()) {
      setError('Headline is required.');
      return;
    }
    const sessionError = validateSessions();
    if (sessionError) {
      setError(sessionError);
      return;
    }
    try {
      if (isEdit && form) {
        await updateMut.mutateAsync({
          formId: form.id,
          body: {
            slug,
            name,
            headline,
            dietary_enabled: dietaryEnabled,
            comments_enabled: commentsEnabled,
            list_ids: listIds,
            sessions: sessionsPayload(),
            contact_email: contactEmail,
            not_on_list_message: notOnListMessage,
            blocked_message: blockedMessage,
          },
        });
      } else {
        await createMut.mutateAsync({
          slug,
          name,
          headline,
          dietary_enabled: dietaryEnabled,
          comments_enabled: commentsEnabled,
          list_ids: listIds,
          sessions: sessionsPayload(),
          contact_email: contactEmail,
          not_on_list_message: notOnListMessage,
          blocked_message: blockedMessage,
        });
      }
      onClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed.');
    }
  }

  async function handleDelete() {
    if (!form) return;
    if (
      !window.confirm(
        `Delete form "${form.name}"? This is irreversible. ` +
        'Active campaigns using this form will block the delete; pause/finish them first.',
      )
    ) {
      return;
    }
    try {
      await deleteMut.mutateAsync(form.id);
      onClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Delete failed.');
    }
  }

  const busy = createMut.isPending || updateMut.isPending;

  // Embed snippet shown only on Edit (need a real saved slug). On
  // Create the slug field is in flux and the URL would 404.
  const embedSnippet = isEdit && form ? embedSnippetForSlug(form.slug) : null;
  const [snippetCopied, setSnippetCopied] = useState(false);

  async function handleCopySnippet() {
    if (!embedSnippet) return;
    const ok = await copyToClipboard(embedSnippet);
    if (ok) {
      setSnippetCopied(true);
      setTimeout(() => setSnippetCopied(false), 2000);
    }
  }

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>
          {isEdit ? `Edit form: ${form?.name}` : 'New form'}
        </h2>
        <button
          type="button"
          className={styles.button}
          onClick={onClose}
        >
          ← Back
        </button>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      <div className={styles.formGrid}>
        <label className={styles.label}>Slug</label>
        <input
          className={styles.input}
          type="text"
          placeholder="agm-rsvp"
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
        />

        <label className={styles.label}>Name</label>
        <input
          className={styles.input}
          type="text"
          placeholder="AGM 2026 RSVP"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />

        <label className={styles.label}>Headline</label>
        <input
          className={styles.input}
          type="text"
          placeholder="Ember Infrastructure AGM 2026 -- RSVP"
          value={headline}
          onChange={(e) => setHeadline(e.target.value)}
        />
      </div>

      <div className={styles.helpText} style={{ marginTop: 4 }}>
        Slug becomes the URL path: <code>/forms/{slug || '<slug>'}</code>.
        Headline is the H1 + browser tab title shown to recipients.
      </div>

      <div style={{ marginTop: 16 }}>
        <div className={styles.label} style={{ marginBottom: 6 }}>
          Optional fields
        </div>
        <label
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            marginRight: 16,
          }}
        >
          <input
            type="checkbox"
            checked={dietaryEnabled}
            onChange={(e) => setDietaryEnabled(e.target.checked)}
          />
          Dietary restrictions textarea
        </label>
        <label
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <input
            type="checkbox"
            checked={commentsEnabled}
            onChange={(e) => setCommentsEnabled(e.target.checked)}
          />
          Comments textarea
        </label>
        <div className={styles.helpText} style={{ marginTop: 4 }}>
          First name, last name, email, and Yes/No are always shown.
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        <div className={styles.label} style={{ marginBottom: 6 }}>
          Linked lists ({listIds.length})
        </div>
        <div className={styles.helpText} style={{ marginBottom: 8 }}>
          Submissions to this form write back to every linked list (status,
          dietary, comments). Linking a 2nd list will prompt for confirmation.
        </div>
        {listsQ.isLoading ? (
          <div className={styles.empty}>Loading lists…</div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {(listsQ.data || []).map((l) => {
              const linked = listIds.includes(l.affinity_list_id);
              return (
                <button
                  key={l.affinity_list_id}
                  type="button"
                  className={`${styles.button} ${linked ? styles.buttonPrimary : ''}`}
                  onClick={() => toggleListId(l.affinity_list_id)}
                  style={{ fontSize: 12 }}
                >
                  {linked ? '✓ ' : ''}
                  {listsByIds.get(l.affinity_list_id) || l.list_slug}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div style={{ marginTop: 24 }}>
        <div className={styles.label} style={{ marginBottom: 6 }}>
          Sessions ({sessions.length})
        </div>
        <div className={styles.helpText} style={{ marginBottom: 8 }}>
          With no sessions the form asks one &ldquo;Will you attend?&rdquo;
          question, exactly as today. Add sessions to ask a separate Yes/No
          per event. The <strong>primary</strong> session&rsquo;s answer is
          the one that moves the list&rsquo;s Status field and proposes the
          calendar invite; the others record into their own Affinity
          dropdown.
        </div>
        {sessions.length > 0 && listIds.length !== 1 && (
          <div className={styles.error} style={{ marginBottom: 8 }}>
            Sessions need exactly one linked list — the per-session Affinity
            fields are list-scoped. This form has {listIds.length}.
          </div>
        )}

        {sessions.map((s, idx) => (
          <div key={idx} className={styles.sessionRow}>
            <div className={styles.sessionRowHeader}>
              <span className={styles.sessionRowTitle}>
                {s.label || `Session ${idx + 1}`}
              </span>
              <label
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  fontSize: 12,
                }}
                title="This session's Yes/No becomes the form's overall attending answer."
              >
                <input
                  type="radio"
                  name="session-primary"
                  checked={s.is_primary}
                  onChange={() => setPrimary(idx)}
                />
                Primary
              </label>
              <button
                type="button"
                className={styles.button}
                onClick={() => moveSession(idx, -1)}
                disabled={idx === 0}
                style={{ fontSize: 12, padding: '2px 8px' }}
                title="Move up"
              >
                &uarr;
              </button>
              <button
                type="button"
                className={styles.button}
                onClick={() => moveSession(idx, 1)}
                disabled={idx === sessions.length - 1}
                style={{ fontSize: 12, padding: '2px 8px' }}
                title="Move down"
              >
                &darr;
              </button>
              <button
                type="button"
                className={`${styles.button} ${styles.buttonDanger}`}
                onClick={() =>
                  setSessions((curr) => curr.filter((_, i) => i !== idx))
                }
                style={{ fontSize: 12, padding: '2px 8px' }}
              >
                Remove
              </button>
            </div>

            <div className={styles.sessionFieldGrid}>
              <div className={styles.sessionField}>
                <span className={styles.label} style={{ marginBottom: 0 }}>
                  Label
                </span>
                <input
                  className={styles.input}
                  type="text"
                  placeholder="Welcome Reception"
                  value={s.label}
                  onChange={(e) => setSessionLabel(idx, e.target.value)}
                />
              </div>
              <div className={styles.sessionField}>
                <span className={styles.label} style={{ marginBottom: 0 }}>
                  When
                </span>
                <input
                  className={styles.input}
                  type="text"
                  placeholder="October 21, 2026 &middot; 5pm &ndash; 8pm"
                  value={s.when_text || ''}
                  onChange={(e) =>
                    patchSession(idx, { when_text: e.target.value })
                  }
                />
              </div>
              <div className={styles.sessionField}>
                <span className={styles.label} style={{ marginBottom: 0 }}>
                  Where
                </span>
                <input
                  className={styles.input}
                  type="text"
                  placeholder="Obvio, 3 E 28th St, New York, NY 10016"
                  value={s.where_text || ''}
                  onChange={(e) =>
                    patchSession(idx, { where_text: e.target.value })
                  }
                />
              </div>
              <div className={styles.sessionField}>
                <span className={styles.label} style={{ marginBottom: 0 }}>
                  Key
                </span>
                <input
                  className={`${styles.input} ${styles.code}`}
                  type="text"
                  placeholder="welcome_reception"
                  value={s.session_key}
                  onChange={(e) =>
                    patchSession(idx, {
                      session_key: e.target.value,
                      keyTouched: true,
                    })
                  }
                  title="Stable key the submitted answers are filed under. Changing it on a live form orphans existing responses."
                />
              </div>
              <div className={styles.sessionField}>
                <span className={styles.label} style={{ marginBottom: 0 }}>
                  Affinity field{s.is_primary ? ' (optional)' : ''}
                </span>
                <select
                  className={styles.input}
                  value={s.affinity_field_id ? String(s.affinity_field_id) : ''}
                  onChange={(e) => pickSessionField(idx, e.target.value)}
                  disabled={singleListId == null}
                >
                  <option value="">
                    {s.is_primary ? '\u2014 none (drives Status) \u2014' : '\u2014 none \u2014'}
                  </option>
                  {sessionFields.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name}
                    </option>
                  ))}
                </select>
                {s.is_primary ? (
                  <span className={styles.helpText}>
                    Leave empty &mdash; this row drives the list Status field.
                  </span>
                ) : singleListId == null ? (
                  <span className={styles.helpText}>
                    Link exactly one list to pick a field.
                  </span>
                ) : sessionFieldsQ.isLoading ? (
                  <span className={styles.helpText}>Loading fields&hellip;</span>
                ) : sessionFields.length === 0 ? (
                  <span className={styles.helpText}>
                    No list-scoped dropdown fields on this list yet.
                  </span>
                ) : null}
              </div>
              {s.affinity_field_id != null && (
                <>
                  <div className={styles.sessionField}>
                    <span className={styles.label} style={{ marginBottom: 0 }}>
                      Yes &rarr; option
                    </span>
                    <select
                      className={styles.input}
                      value={s.yes_option_label}
                      onChange={(e) =>
                        patchSession(idx, { yes_option_label: e.target.value })
                      }
                    >
                      <option value={OPTION_UNSET}>&mdash; pick one &mdash;</option>
                      {Object.keys(s.dropdown_options).map((label) => (
                        <option key={label} value={label}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className={styles.sessionField}>
                    <span className={styles.label} style={{ marginBottom: 0 }}>
                      No &rarr; option
                    </span>
                    <select
                      className={styles.input}
                      value={s.no_option_label}
                      onChange={(e) =>
                        patchSession(idx, { no_option_label: e.target.value })
                      }
                    >
                      <option value={OPTION_UNSET}>&mdash; pick one &mdash;</option>
                      {Object.keys(s.dropdown_options).map((label) => (
                        <option key={label} value={label}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}
            </div>
          </div>
        ))}

        <button
          type="button"
          className={styles.button}
          onClick={() =>
            setSessions((curr) => [
              ...curr,
              // The first row added becomes primary by default: the common
              // case is one row, and a form can never save with zero.
              { ...emptySession(), is_primary: curr.length === 0 },
            ])
          }
          style={{ fontSize: 12 }}
        >
          + Add session
        </button>
      </div>

      <div style={{ marginTop: 24 }}>
        <div className={styles.label} style={{ marginBottom: 6 }}>
          Contact email (optional)
        </div>
        <input
          className={styles.input}
          type="email"
          placeholder="agm@ember-infra.com"
          value={contactEmail}
          onChange={(e) => setContactEmail(e.target.value)}
        />
        <div className={styles.helpText} style={{ marginTop: 4 }}>
          Substituted into the canned error messages below + used as the
          sender / recipient of unmatched-submission notifications. Leave
          blank to use the default <code>agm@ember-infra.com</code>.
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        <div className={styles.label} style={{ marginBottom: 6 }}>
          &ldquo;Not on the list&rdquo; message (optional)
        </div>
        <textarea
          className={styles.input}
          rows={3}
          placeholder={
            `Default: We don't have this email on the list. ` +
            `If you should be invited, please email ` +
            `${contactEmail || 'agm@ember-infra.com'} and we'll add you.`
          }
          value={notOnListMessage}
          onChange={(e) => setNotOnListMessage(e.target.value)}
          style={{ minHeight: 64 }}
        />
        <div className={styles.helpText} style={{ marginTop: 4 }}>
          Shown when a submitted email doesn&rsquo;t match any active
          recipient on any linked list. Leave blank to use the default
          (with the contact email above substituted in).
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        <div className={styles.label} style={{ marginBottom: 6 }}>
          &ldquo;Recipient blocked&rdquo; message (optional)
        </div>
        <textarea
          className={styles.input}
          rows={3}
          placeholder={
            `Default: This recipient is currently flagged for manual ` +
            `handling. Please email ` +
            `${contactEmail || 'agm@ember-infra.com'} so we can sort it out.`
          }
          value={blockedMessage}
          onChange={(e) => setBlockedMessage(e.target.value)}
          style={{ minHeight: 64 }}
        />
        <div className={styles.helpText} style={{ marginTop: 4 }}>
          Shown when the recipient IS on the list but has been paused /
          suppressed. Leave blank to use the default.
        </div>
      </div>

      {embedSnippet && (
        <div style={{ marginTop: 24 }}>
          <div className={styles.label} style={{ marginBottom: 6 }}>
            Embed code
          </div>
          <div className={styles.helpText} style={{ marginBottom: 8 }}>
            Paste this <code>&lt;iframe&gt;</code> into any page on{' '}
            <code>ember-infra.com</code> (or any origin allowed by the
            form's CSP) to embed the public RSVP form. The form opens in
            its public, no-token mode — recipients type their email,
            we match it against the linked list(s).
          </div>
          <div
            style={{
              display: 'flex', alignItems: 'flex-start', gap: 8,
            }}
          >
            <pre
              className={styles.code}
              style={{
                flex: 1,
                margin: 0,
                padding: 8,
                background: '#FAF9F4',
                borderRadius: 6,
                fontSize: 12,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
              }}
            >
              {embedSnippet}
            </pre>
            <button
              type="button"
              className={styles.button}
              onClick={handleCopySnippet}
              style={{ flexShrink: 0 }}
            >
              {snippetCopied ? '✓ Copied' : 'Copy'}
            </button>
          </div>
        </div>
      )}

      <div
        style={{
          display: 'flex', gap: 8, marginTop: 24, justifyContent: 'flex-end',
        }}
      >
        {isEdit && (
          <button
            type="button"
            className={`${styles.button} ${styles.buttonDanger}`}
            onClick={handleDelete}
            disabled={deleteMut.isPending}
            style={{ marginRight: 'auto' }}
          >
            {deleteMut.isPending ? 'Deleting…' : 'Delete form'}
          </button>
        )}
        <button type="button" className={styles.button} onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className={`${styles.button} ${styles.buttonPrimary}`}
          onClick={handleSave}
          disabled={busy}
        >
          {busy ? 'Saving…' : isEdit ? 'Save changes' : 'Create form'}
        </button>
      </div>
    </div>
  );
}
